import { ModalProps } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { presentationSlotTypes } from '../../../../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../../../../reduxToolkit/typedRedux';
import { createPresentation, createStudio } from '../../../../../../../../services/services';
import VyButton from '../../../../../../../common/VyButton';
import VyModal from '../../../../../../../common/VyModal';
import VyTextField from '../../../../../../../common/VyTextField';
import { BoothSelect } from './BoothSelect';
import { SeatSelect } from './SeatSelect';

import { SupportedFunctions, useEventFunctions } from '../../../../../../../../hooks/useEventFunctions';
import { StreamTypeSelect } from './StreamTypeSelect';
import { TypeSelect } from './TypeSelect';

interface CreatePresentationModalProps extends Omit<Omit<ModalProps, 'children'>, 'onClose'> {
  onClose: () => void;
  onCreate: () => void;
}

interface FormProps {
  name: string;
  title: string;
  booth: string;
  presentationSlotType?: presentationSlotTypes;
  presentationMaxSeats?: string;
  presentationRequireSignup?: boolean;
  videoType?: 'livestream' | 'videoconference';
}

export const CreatePresentationModal = ({ slot, onClose, onCreate, ...modalProps }: CreatePresentationModalProps) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const methods = useForm<FormProps>();
  const { register, handleSubmit, watch } = methods;
  const { isFunctionEnabled } = useEventFunctions();

  const event = useSelector((state) => state.admin.event ?? state.common.event);

  const formSuccess = async ({ name, title, booth, presentationSlotType, presentationMaxSeats, presentationRequireSignup, videoType }: FormProps) => {
    if (!event) return;
    setLoading(true);
    try {
      if (videoType === 'livestream') {
        await createStudio(event._id, {
          isStudioForPresentationSlot: true,
          contactperson: name,
          name: title,
          booth: typeof booth !== 'string' ? null : booth,
        });
      } else {
        await createPresentation(
          booth || undefined,
          name,
          title,
          presentationSlotType,
          parseInt(presentationMaxSeats ?? '0'),
          presentationRequireSignup,
          event?._id
        );
      }
      toast.success(t('exhibitors:presentation-created'));
      onClose();

      onCreate();
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  };

  const formError = (error: any) => {
    console.error(error);
  };

  function getInitialVideoType() {
    const livestreamEnabled = isFunctionEnabled(SupportedFunctions.livestreams, event);

    if (livestreamEnabled) return 'livestream';
    else return 'videoconference';
  }

  const watchPresentationSlotType: presentationSlotTypes | undefined = watch('presentationSlotType');
  const watchVideoType: 'livestream' | 'videoconference' | undefined = watch('videoType', getInitialVideoType());

  return (
    <VyModal open={modalProps.open} onClose={onClose} style={{ width: '40vw' }}>
      {(onClose) => (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(formSuccess, formError)}>
            <div className="flex flex-col space-y-3 break-words">
              <p className="text-xl font-bold text-slate-800">{t('account:new-presentation')}</p>
              <TypeSelect />
              {watchPresentationSlotType && (
                <div className="flex flex-col space-y-3 break-words">
                  <VyTextField
                    register={register('title', { required: true })}
                    label={t('account:presentation-title')}
                    inputProps={{ maxLength: 250 }}
                    fullWidth
                  />
                  <VyTextField register={register('name', { required: true })} label={t('account:speaker-name')} inputProps={{ maxLength: 100 }} fullWidth />
                  {watchPresentationSlotType !== 'online' && (
                    <>
                      <hr />
                      <SeatSelect presentationSlotType={watchPresentationSlotType} />
                    </>
                  )}
                  {watchPresentationSlotType !== 'on-site' && <StreamTypeSelect defaultValue={getInitialVideoType()} />}
                  {watchPresentationSlotType !== 'on-site' && (
                    <>
                      <hr />
                      <BoothSelect presentationSlotType={watchPresentationSlotType} videoType={watchVideoType ?? getInitialVideoType()} />
                    </>
                  )}

                  <div className="flex justify-between mt-2">
                    <VyButton ghost className="mt-2" onClick={() => onClose()} disabled={loading}>
                      {t('account:cancel')}
                    </VyButton>
                    <VyButton type="submit" className="mt-2 ml-2" disabled={loading} showLoadingIcon={loading}>
                      {t('account:create')}
                    </VyButton>
                  </div>
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      )}
    </VyModal>
  );
};
